import React, { useState, useEffect } from "react";
import { useAuthStore } from "../../../store/authStore";
import { supabase } from "../../../lib/supabase";
import { useToast } from "../../../hooks/useToast";
import { emailService } from "../../../services/emailService";
import { Card } from "../../Shared/Card";
import { Button } from "../../Shared/Button";
import {
  UserIcon,
  EnvelopeIcon,
  KeyIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline";

export const AccountSettings = () => {
  const user = useAuthStore((state) => state.user);
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { showToast } = useToast();
  const [formData, setFormData] = useState({
    email: user?.email || "",
    firstName: user?.user_metadata?.first_name || "",
    lastName: user?.user_metadata?.last_name || "",
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [validations, setValidations] = useState({
    minLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false,
    passwordsMatch: false,
  });
  const [passwordErrors, setPasswordErrors] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  useEffect(() => {
    const password = passwordData.newPassword;
    setValidations({
      minLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      passwordsMatch:
        password === passwordData.confirmPassword && password !== "",
    });
  }, [passwordData.newPassword, passwordData.confirmPassword]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.id]: e.target.value,
    }));
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));

    // Clear errors when user starts typing
    if (passwordErrors[e.target.name as keyof typeof passwordErrors]) {
      setPasswordErrors((prev) => ({
        ...prev,
        [e.target.name]: "",
      }));
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (formData.email !== user?.email) {
        const { error } = await supabase.auth.updateUser(
          {
            email: formData.email,
          },
          {
            emailRedirectTo: `${window.location.origin}/?mode=dashboard&section=settings`,
          }
        );
        if (error) throw error;
        showToast("Verification email sent to new address", "success");
      }

      const { error: updateError } = await supabase.auth.updateUser({
        data: {
          first_name: formData.firstName,
          last_name: formData.lastName,
        },
      });

      if (updateError) throw updateError;
      showToast("Profile updated successfully", "success");
    } catch (error) {
      showToast("Failed to update profile", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdatePassword = async () => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(
        user?.email!,
        {
          redirectTo: `${window.location.origin}/?mode=dashboard&section=settings`,
        }
      );

      if (error) throw error;
      showToast("Password reset email sent", "success");
    } catch (error) {
      showToast("Failed to send reset email", "error");
    }
  };

  const validatePasswordChange = () => {
    const errors = {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    };

    if (!passwordData.currentPassword) {
      errors.currentPassword = "Current password is required";
    }

    if (!passwordData.newPassword) {
      errors.newPassword = "New password is required";
    } else if (!Object.values(validations).every((v) => v)) {
      errors.newPassword = "Password does not meet requirements";
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    setPasswordErrors(errors);
    return !Object.values(errors).some((error) => error);
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validatePasswordChange()) {
      return;
    }

    setIsPasswordLoading(true);

    try {
      // First verify current password
      const { error: verifyError } = await supabase.auth.signInWithPassword({
        email: user?.email!,
        password: passwordData.currentPassword,
      });

      if (verifyError) {
        setPasswordErrors((prev) => ({
          ...prev,
          currentPassword: "Current password is incorrect",
        }));
        return;
      }

      // Update password
      const { error } = await supabase.auth.updateUser({
        password: passwordData.newPassword,
      });

      if (error) throw error;

      await emailService.sendPasswordChangeNotification(user?.email!);
      showToast("Password updated successfully", "success");

      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setPasswordErrors({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      showToast("Failed to update password", "error");
    } finally {
      setIsPasswordLoading(false);
    }
  };

  const PasswordRequirement = ({
    met,
    children,
  }: {
    met: boolean;
    children: React.ReactNode;
  }) => (
    <div
      className={`flex items-center gap-2 text-xs transition-colors ${
        met ? "text-green-600" : "text-gray-500"
      }`}
    >
      <div
        className={`w-1 h-1 rounded-full ${
          met ? "bg-green-500" : "bg-gray-300"
        }`}
      />
      {children}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Profile Information */}
      <Card className="p-0 overflow-hidden">
        <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg shadow-lg">
              <UserIcon className="h-4 w-4 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Profile Information
              </h2>
              <p className="text-xs text-gray-500">
                Update your account details and preferences
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleUpdateProfile} className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="firstName"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                First Name
              </label>
              <input
                type="text"
                id="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                placeholder="Enter your first name"
              />
            </div>

            <div>
              <label
                htmlFor="lastName"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Last Name
              </label>
              <input
                type="text"
                id="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                placeholder="Enter your last name"
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Email Address
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <EnvelopeIcon className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                placeholder="Enter your email address"
              />
            </div>
            {formData.email !== user?.email && (
              <p className="mt-1 text-xs text-amber-600 flex items-center gap-1">
                <ExclamationTriangleIcon className="h-3 w-3" />
                You'll need to verify your new email address
              </p>
            )}
          </div>

          <div className="flex justify-end pt-2">
            <Button
              type="submit"
              isLoading={isLoading}
              className="px-4 py-2 text-sm"
            >
              Update Profile
            </Button>
          </div>
        </form>
      </Card>

      {/* Password Security */}
      <Card className="p-0 overflow-hidden">
        <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg shadow-lg">
              <ShieldCheckIcon className="h-4 w-4 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Password & Security
              </h2>
              <p className="text-xs text-gray-500">
                Keep your account secure with a strong password
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handlePasswordSubmit} className="p-4 space-y-4">
          <div>
            <label
              htmlFor="currentPassword"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Current Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <KeyIcon className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type={showCurrentPassword ? "text" : "password"}
                name="currentPassword"
                value={passwordData.currentPassword}
                onChange={handlePasswordChange}
                className={`w-full pl-9 pr-10 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                  passwordErrors.currentPassword
                    ? "border-red-300"
                    : "border-gray-300"
                }`}
                placeholder="Enter your current password"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showCurrentPassword ? (
                  <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                ) : (
                  <EyeIcon className="h-4 w-4 text-gray-400" />
                )}
              </button>
            </div>
            {passwordErrors.currentPassword && (
              <p className="mt-1 text-xs text-red-600">
                {passwordErrors.currentPassword}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="newPassword"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              New Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <KeyIcon className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type={showNewPassword ? "text" : "password"}
                name="newPassword"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                className={`w-full pl-9 pr-10 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                  passwordErrors.newPassword
                    ? "border-red-300"
                    : "border-gray-300"
                }`}
                placeholder="Enter your new password"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showNewPassword ? (
                  <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                ) : (
                  <EyeIcon className="h-4 w-4 text-gray-400" />
                )}
              </button>
            </div>
            {passwordErrors.newPassword && (
              <p className="mt-1 text-xs text-red-600">
                {passwordErrors.newPassword}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Confirm New Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <KeyIcon className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
                className={`w-full pl-9 pr-10 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                  passwordErrors.confirmPassword
                    ? "border-red-300"
                    : "border-gray-300"
                }`}
                placeholder="Confirm your new password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showConfirmPassword ? (
                  <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                ) : (
                  <EyeIcon className="h-4 w-4 text-gray-400" />
                )}
              </button>
            </div>
            {passwordErrors.confirmPassword && (
              <p className="mt-1 text-xs text-red-600">
                {passwordErrors.confirmPassword}
              </p>
            )}
          </div>

          {passwordData.newPassword && (
            <div className="bg-gray-50 rounded-lg p-3">
              <h4 className="text-xs font-medium text-gray-900 mb-2">
                Password Requirements
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                <PasswordRequirement met={validations.minLength}>
                  At least 8 characters
                </PasswordRequirement>
                <PasswordRequirement met={validations.hasUpperCase}>
                  One uppercase letter
                </PasswordRequirement>
                <PasswordRequirement met={validations.hasLowerCase}>
                  One lowercase letter
                </PasswordRequirement>
                <PasswordRequirement met={validations.hasNumber}>
                  One number
                </PasswordRequirement>
                <PasswordRequirement met={validations.hasSpecialChar}>
                  One special character
                </PasswordRequirement>
                <PasswordRequirement met={validations.passwordsMatch}>
                  Passwords match
                </PasswordRequirement>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2 sm:justify-between pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleUpdatePassword}
              className="px-4 py-2 text-sm"
            >
              Send Reset Email
            </Button>

            <Button
              type="submit"
              isLoading={isPasswordLoading}
              disabled={!Object.values(validations).every((v) => v)}
              className="px-4 py-2 text-sm"
            >
              Update Password
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};
